import React, { useState } from "react";
import Icon from "./Icon";
import { formatYmd } from "../dateutil";
import { C, R, S } from "../theme";

const iso = (d) => { const z = new Date(d.getTime() - d.getTimezoneOffset() * 60000); return z.toISOString().slice(0, 10); };
const WD = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const CHIPS = [["all", "All time"], ["today", "Today"], ["yesterday", "Yesterday"], ["7", "Last 7 days"], ["30", "Last 30 days"], ["single", "Single day"], ["custom", "Custom"]];

// Unified date-range control used across all report/export screens.
// Emits onChange(fromYmd|null, toYmd|null). UK display (DD/MM/YYYY).
export default function DateRangePicker({ from, to, onChange }) {
  const [mode, setMode] = useState(from || to ? "custom" : "all");
  const start = new Date();
  const [view, setView] = useState(new Date(start.getFullYear(), start.getMonth(), 1));

  const preset = (m) => {
    setMode(m);
    const now = new Date(); const t = iso(now);
    if (m === "all") return onChange(null, null);
    if (m === "today") return onChange(t, t);
    if (m === "yesterday") { const yd = new Date(now); yd.setDate(yd.getDate() - 1); const y1 = iso(yd); return onChange(y1, y1); }
    if (m === "7") { const f = new Date(now); f.setDate(f.getDate() - 6); return onChange(iso(f), t); }
    if (m === "30") { const f = new Date(now); f.setDate(f.getDate() - 29); return onChange(iso(f), t); }
    // single / custom: reveal the calendar, keep current selection
  };

  const pick = (d) => {
    const ymd = iso(d);
    if (mode === "single") return onChange(ymd, ymd);
    if (!from || (from && to)) onChange(ymd, null);
    else if (ymd < from) onChange(ymd, from);
    else onChange(from, ymd);
  };

  const y = view.getFullYear(), mo = view.getMonth();
  const startDow = (new Date(y, mo, 1).getDay() + 6) % 7;
  const dim = new Date(y, mo + 1, 0).getDate();
  const cells = []; for (let i = 0; i < startDow; i++) cells.push(null); for (let d = 1; d <= dim; d++) cells.push(new Date(y, mo, d));
  const inRange = (ymd) => from && to && ymd >= from && ymd <= to;

  return (
    <div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: S.sm, marginBottom: S.sm }}>
        {CHIPS.map(([k, l]) => (
          <button key={k} data-testid={`range-${k}`} onClick={() => preset(k)} style={{ padding: "8px 14px", borderRadius: R.pill, background: mode === k ? C.amber : C.card2, color: mode === k ? C.black : C.sub, fontWeight: 800, fontSize: 13, border: "none" }}>{l}</button>
        ))}
      </div>
      {(from || to) && <div data-testid="range-label" style={{ color: C.amber, fontSize: 13, fontWeight: 700, marginBottom: S.sm }}>{from && to && from === to ? formatYmd(from) : `${from ? formatYmd(from) : "…"} → ${to ? formatYmd(to) : "select end date"}`}</div>}
      {(mode === "custom" || mode === "single") && (
        <div data-testid="range-calendar" style={{ background: C.card2, borderRadius: R.md, border: `1px solid ${C.borderStrong}`, padding: S.md, marginBottom: S.sm }}>
          <div style={{ display: "flex", gap: S.sm, marginBottom: S.md }}>
            <div data-testid="range-from-box" style={{ flex: 1, background: from ? "rgba(245,158,11,0.15)" : C.card, borderRadius: R.md, border: `1.5px solid ${from ? C.amber : C.border}`, padding: `${S.sm}px ${S.md}px` }}>
              <div style={{ color: C.muted, fontSize: 10, fontWeight: 800, letterSpacing: 1 }}>FROM</div>
              <div style={{ color: from ? C.text : C.muted, fontSize: 15, fontWeight: 800 }}>{from ? formatYmd(from) : "—"}</div>
            </div>
            <div data-testid="range-to-box" style={{ flex: 1, background: to ? "rgba(245,158,11,0.15)" : C.card, borderRadius: R.md, border: `1.5px solid ${to ? C.amber : C.border}`, padding: `${S.sm}px ${S.md}px` }}>
              <div style={{ color: C.muted, fontSize: 10, fontWeight: 800, letterSpacing: 1 }}>TO</div>
              <div style={{ color: to ? C.text : C.muted, fontSize: 15, fontWeight: 800 }}>{to ? formatYmd(to) : "—"}</div>
            </div>
          </div>
          <div style={{ color: C.muted, fontSize: 12, marginBottom: S.sm, textAlign: "center" }}>{!from || (from && to) ? "Tap a start date" : "Now tap the end date"}</div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: S.sm }}>
            <button data-testid="cal-prev" onClick={() => setView(new Date(y, mo - 1, 1))} className="pressable" style={{ background: C.card, borderRadius: R.md, border: `1px solid ${C.border}`, width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center" }}><Icon name="chevron-back" size={22} color={C.text} /></button>
            <div style={{ color: C.text, fontWeight: 800, fontSize: 16 }}>{MONTHS[mo]} {y}</div>
            <button data-testid="cal-next" onClick={() => setView(new Date(y, mo + 1, 1))} className="pressable" style={{ background: C.card, borderRadius: R.md, border: `1px solid ${C.border}`, width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center" }}><Icon name="chevron-forward" size={22} color={C.text} /></button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 3, marginBottom: 6 }}>
            {WD.map((w) => <div key={w} style={{ textAlign: "center", color: C.muted, fontSize: 11, fontWeight: 800 }}>{w}</div>)}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 3 }}>
            {cells.map((d, i) => {
              if (!d) return <div key={i} />;
              const ymd = iso(d); const sel = ymd === from || ymd === to; const rng = inRange(ymd); const today = ymd === iso(new Date());
              return <button key={i} data-testid={`day-${ymd}`} onClick={() => pick(d)} className="pressable" style={{ minHeight: 40, aspectRatio: "1", borderRadius: 10, border: today && !sel ? `1.5px solid ${C.amber}` : "none", background: sel ? C.amber : rng ? "rgba(245,158,11,0.22)" : C.card, color: sel ? C.black : C.text, fontWeight: sel ? 900 : 600, fontSize: 14, cursor: "pointer" }}>{d.getDate()}</button>;
            })}
          </div>
          <button data-testid="cal-reset" onClick={() => onChange(null, null)} className="pressable" style={{ marginTop: S.md, width: "100%", background: "transparent", border: "none", color: C.muted, fontWeight: 700, fontSize: 13 }}>Clear dates</button>
        </div>
      )}
    </div>
  );
}
